import { TypedDocumentNode, gql } from '@apollo/client';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { SportBall } from '@sorare/core/src/atoms/icons/SportBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ANY_SPORT_PLAY_ADVENT_CALENDAR } from '@sorare/core/src/constants/__generated__/routes';
import { isType } from '@sorare/core/src/gql';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useCurrentSport } from '@sorare/core/src/hooks/useCurrentSport';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useLifecycle, { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { useProbabilisticBundleConfigSummary } from '@sorare/core/src/hooks/useProbabilisticBundleConfigSummary';
import { fantasy, filters, glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BoldWhite } from '../Explanation/BoldWhite';
import { NeonChristmasBackground } from '../NeonChristmasBackground';
import { RewardName } from '../RewardName';
import starUrl from '../assets/star.svg';
import { sortRewards } from '../utils';
import { NeonChristmasMarketplaceBanner_PromotionalEventInterface } from './__generated__/index.graphql';

const Wrapper = styled(LinkBox)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  padding: var(--quadruple-unit) var(--quadruple-unit) var(--quadruple-unit)
    var(--double-unit);

  position: relative;
`;

const ContentWrapper = styled(Vertical)`
  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;

const Explain = ({
  event,
}: {
  event: NeonChristmasMarketplaceBanner_PromotionalEventInterface;
}) => {
  const { formatMessage } = useIntl();
  if (isType(event, 'MarketPromotionalEvent')) {
    const summary = useProbabilisticBundleConfigSummary(
      event.rewardsProbabilisticBundleConfig[0]
    );
    const [firstReward] = sortRewards(summary);

    const reward = (() => {
      if (firstReward.name === formatMessage(glossary.cards)) {
        return (
          <FormattedMessage
            id="NeonChristmas.explanation.reward.cards"
            defaultMessage="2nd player"
          />
        );
      }
      if (firstReward.name === formatMessage(fantasy.credits)) {
        return (
          <FormattedMessage
            id="NeonChristmas.explanation.reward.credits"
            defaultMessage="Conversion credits"
          />
        );
      }
      if (firstReward.name.match(/Level Up/i)) {
        return (
          <FormattedMessage
            id="ShopitemPicker.FilterBy.LevelUp"
            defaultMessage="Level up"
          />
        );
      }
      return firstReward.name;
    })();

    return <RewardName small>{reward}</RewardName>;
  }
  if (isType(event, 'AirdropPromotionalEvent')) {
    if (event.claimed)
      return (
        <FormattedMessage
          id="NeonChristmas.banner.freeBox.claimed"
          defaultMessage="You got your <b>free box</b>!"
          values={{
            b: (chunks: ReactNode[]) => <RewardName small>{chunks}</RewardName>,
          }}
        />
      );
    return (
      <FormattedMessage
        id="NeonChristmas.banner.freeBox.claim"
        defaultMessage="Claim your <b>free box</b>!"
        values={{
          b: (chunks: ReactNode[]) => <RewardName small>{chunks}</RewardName>,
        }}
      />
    );
  }
  if (isType(event, 'SpecialEditionPromotionalEvent')) {
    return (
      <div>
        <FormattedMessage
          id="NeonChristmas.banner.specialEdition"
          defaultMessage="<b>{probability, number, percent}</b> chance the card you buy is a special edition"
          values={{
            probability: (event.revealableCardConfig ?? [])[0]
              ?.revealedEditionsConfig[0]?.odds,
            b: BoldWhite,
          }}
        />
      </div>
    );
  }
  return <span />;
};

export const NeonChristmasMarketplaceBanner = ({
  events,
}: {
  events: NeonChristmasMarketplaceBanner_PromotionalEventInterface[];
}) => {
  const {
    flags: {
      useNeonChristmas = false,
      // add a flag to quickly disable the feature if needed
      useOpenAdventCalendar = true,
    },
  } = useFeatureFlags();
  const sport = useCurrentSport();

  const filteredEvents = events.filter(
    e =>
      isType(e, 'MarketPromotionalEvent') ||
      isType(e, 'AirdropPromotionalEvent') ||
      isType(e, 'SpecialEditionPromotionalEvent')
  );

  const event = filteredEvents[0];

  const currentDay = event?.startDate.getUTCDate();
  const { lifecycle } = useLifecycle();
  const openedDays = lifecycle?.[LIFECYCLE.neonChristmasCalendar] ?? [];
  const isOpened = openedDays.includes(currentDay);

  const isTabletAndAbove = useIsTabletAndAbove();
  if (currentDay && !isOpened && useOpenAdventCalendar) {
    return (
      <Navigate
        to={generateSportPath(ANY_SPORT_PLAY_ADVENT_CALENDAR, { sport })}
      />
    );
  }

  if (!useNeonChristmas || !event) {
    return null;
  }

  return (
    <Wrapper>
      <NeonChristmasBackground
        style={
          {
            backgroundImage: `linear-gradient(
              to right,
              rgba(16, 16, 16, 0.5) 0%,
              rgba(16, 16, 16, 0) 100%),url(${starUrl})`,
            backgroundSize: 'cover, 800px 800px',
            backgroundPosition: 'left top, -400px -335px',
            backgroundRepeat: 'no-repeat',

            position: 'absolute',
            inset: 0,
            zIndex: -1,
          } as React.CSSProperties
        }
      />
      <ContentWrapper>
        <Horizontal>
          <Chip>
            <FormattedDate
              value={event.startDate}
              month="short"
              day="numeric"
            />
          </Chip>
          <SportBall sport={event.sport} />
        </Horizontal>
        <Explain event={event} />
      </ContentWrapper>

      <Horizontal center gap={4}>
        <LinkOverlay
          as={Link}
          to={generateSportPath(ANY_SPORT_PLAY_ADVENT_CALENDAR, { sport })}
        >
          {isTabletAndAbove && (
            <>
              <FormattedMessage {...filters.showDetailsLabel} />{' '}
            </>
          )}
          <FontAwesomeIcon icon={faCaretRight} />
        </LinkOverlay>
      </Horizontal>
    </Wrapper>
  );
};

NeonChristmasMarketplaceBanner.fragments = {
  PromotionalEventInterface: gql`
    fragment NeonChristmasMarketplaceBanner_PromotionalEventInterface on PromotionalEventInterface {
      slug
      name
      startDate
      endDate
      termsAndConditionsUrl
      ... on AirdropPromotionalEvent {
        slug
        sport
        claimed
      }
      ... on MarketPromotionalEvent {
        slug
        sport
        cardFromDomesticLeague
        cardFromSameTeam
        rewardsProbabilisticBundleConfig {
          id
          ...useProbabilisticBundleConfigSummary_ProbabilisticBundleConfig
        }
      }
      ... on SpecialEditionPromotionalEvent {
        slug
        revealableCardConfig {
          revealedEditionsConfig {
            odds
          }
        }
        sport
      }
    }

    ${useProbabilisticBundleConfigSummary.fragments.ProbabilisticBundleConfig}
  ` as TypedDocumentNode<NeonChristmasMarketplaceBanner_PromotionalEventInterface>,
};
